import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { findAnswerableMessageId } from '../core/messages'
import { loadStorage, scheduleSave } from '../core/storage'
import { retryMessage, sendFile, sendText } from '../core/transport'
import { t } from '../i18n'
import {
	connectionStatus,
	currentOperator,
	lightboxAlt,
	lightboxUrl,
	messages,
	operatorTyping,
	runtimeConfig,
	sessionClosed,
	startNewSession,
} from '../state/widget'
import type { ChatMessage } from '../types/public'
import {
	AlertCircleIcon,
	BackIcon,
	CheckDoubleIcon,
	CheckSingleIcon,
	CloseIcon,
	PaperclipIcon,
	SendIcon,
} from './Icons'

type Props = {
	onClose: () => void
	onBack: () => void
	isMobile?: boolean
	panelClass?: string
	token: string
}

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50 MB
const IMAGE_EXT_RE =
	/\.(jpe?g|png|apng|gif|webp|bmp|ico|heic|jxl|tiff?|svg|avif)(?:[?#]|$)/i

function looksLikeImage(message: ChatMessage): boolean {
	const mime = message.file?.mime ?? ''
	if (mime.startsWith('image/')) return true
	const name = message.file?.name ?? message.fileName ?? message.fileUrl ?? ''
	return IMAGE_EXT_RE.test(name)
}
// Источник истины — backend: src/common/constants/file.constants.ts (ALLOWED_FILE_EXTENSIONS).
// Держим синхронным с cp-vite/src/features/send-message/config/constants.ts.
// Опасные форматы (SVG — XSS; PDF — JS; DOC/XLS — VBA; ZIP — opaque) намеренно не включены.
const ACCEPT_MIME =
	'.jpg,.png,.apng,.gif,.webp,.flif,.tif,.bmp,.icns,.jxr,.psd,.rtf,.epub,.mobi,.ico,.xml,.heic,.cur,.ktx,.aif,.wav,.flac,.mp3,.ogg,.opus,.voc,.ac3,.m4a,.spx,.shp,.aac,.mp1,.it,.s3m,.xm,.stl,.3mf,.jxl,.vcf,.docx,.mp4,.mkv,.mov,.webm,.xlsx,.pptx,.odt,.ods,.odp'

export function ChatActive({
	onClose,
	onBack,
	isMobile = false,
	panelClass = '',
	token,
}: Props) {
	const list = messages.value
	const operator = currentOperator.value
	const typing = operatorTyping.value
	const conn = connectionStatus.value
	const cfg = runtimeConfig.value

	const bodyRef = useRef<HTMLDivElement>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const [draft, setDraft] = useState(() => loadStorage(token).prefs.draft?.text ?? '')
	const [fileError, setFileError] = useState<string | null>(null)
	const [unreadCount, setUnreadCount] = useState(0)
	const isNearBottomRef = useRef(true)
	const prevLenRef = useRef(list.length)
	const unreadCountRef = useRef(unreadCount)
	const NEAR_BOTTOM_PX = 80
	// Touch-устройство: pointer:coarse + hover:none — типичный смартфон/планшет.
	// Desktop с тачскрином hover:hover — там оставляем submit-on-Enter.
	const isTouchOnly = useMemo(
		() =>
			typeof window !== 'undefined' &&
			window.matchMedia('(pointer: coarse) and (hover: none)').matches,
		[],
	)

	// Высоту панели на мобиле подгоняет родительский layer через
	// --widget-viewport-h (см. app.tsx → useVisualViewport).
	const panelStyle = isMobile ? ({ width: '100%' } as const) : { width: 380, height: 560 }

	const headerTitle = operator?.name ?? cfg?.brand ?? t('chat.title')
	const headerSub = useMemo(() => {
		if (conn === 'reconnecting' || conn === 'offline')
			return t('chat.reconnecting')
		if (typing) return t('chat.typing')
		if (operator) return t('chat.online')
		return t('chat.typical_reply')
	}, [conn, typing, operator])

	const greeting = cfg?.greeting ?? t('chat.greeting_fallback')
	const closed = sessionClosed.value
	const showGreeting = list.length === 0 && !closed

	// Effect A — новое сообщение: скроллим если были внизу или сообщение наше
	useEffect(() => {
		const prev = prevLenRef.current
		prevLenRef.current = list.length
		if (list.length <= prev) return

		const lastMsg = list[list.length - 1]
		const isMine = lastMsg?.direction === 'out'
		const wasNearBottom = isNearBottomRef.current

		if (isMine || wasNearBottom) {
			bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight })
			setUnreadCount(0)
			return
		}
		setUnreadCount((c) => c + (list.length - prev))
	}, [list.length])

	// Effect B — индикатор набора / закрытие сессии: подскроливаем если были внизу
	useEffect(() => {
		if (isNearBottomRef.current) {
			bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight })
		}
	}, [typing, closed])

	// Mirror unreadCount to ref — для использования в scroll listener
	useEffect(() => {
		unreadCountRef.current = unreadCount
	}, [unreadCount])

	// Scroll listener — отслеживаем близость к низу, сбрасываем счётчик при возврате
	useEffect(() => {
		const el = bodyRef.current
		if (!el) return
		const onScroll = () => {
			const near = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
			isNearBottomRef.current = near
			if (near && unreadCountRef.current > 0) setUnreadCount(0)
		}
		el.addEventListener('scroll', onScroll, { passive: true })
		return () => el.removeEventListener('scroll', onScroll)
	}, [])

	// Авто-высота textarea: пересчёт при изменении draft. max-height ограничен
	// CSS-ом (120px), при превышении появляется внутренний scroll.
	useEffect(() => {
		const ta = textareaRef.current
		if (!ta) return
		ta.style.height = 'auto'
		ta.style.height = `${ta.scrollHeight}px`
	}, [draft])

	// Когда клавиатура iOS открывается/закрывается — визуальный viewport
	// меняется, и сообщения могут "уехать". Подскроливаем к низу.
	useEffect(() => {
		if (!isMobile) return
		const vv = window.visualViewport
		if (!vv) return
		const onResize = () => {
			if (bodyRef.current) {
				bodyRef.current.scrollTop = bodyRef.current.scrollHeight
			}
		}
		vv.addEventListener('resize', onResize)
		return () => vv.removeEventListener('resize', onResize)
	}, [isMobile])

	// Очищаем draft в storage при закрытии сессии.
	useEffect(() => {
		if (closed && token) {
			const current = loadStorage(token)
			if (current.prefs.draft) {
				scheduleSave(token, {
					...current,
					prefs: { ...current.prefs, draft: undefined },
				})
			}
		}
	}, [closed, token])

	// Блокируем ввод и отправку во всех случаях кроме `online`:
	// - connecting/reconnecting/offline — бэк может не принять сообщение;
	// - idle — ещё не инициализировали сессию.
	// Пользователь не должен отправлять в "никуда": сообщение могло бы попасть
	// в outbound-очередь, но при длительном offline всё равно помечается failed.
	const inputDisabled = closed || conn !== 'online'
	// Кнопки ответа не блокируются закрытым диалогом: опрос CSI приходит уже
	// после «Диалог завершён», и ответить на него можно только кнопкой.
	const answerableId = useMemo(() => findAnswerableMessageId(list), [list])
	const optionsDisabled = conn !== 'online'

	function handleOptionClick(option: string) {
		if (optionsDisabled) return
		sendText(option, { allowWhenClosed: true })
	}
	const canSend = draft.trim().length > 0 && !inputDisabled
	const placeholder = closed
		? t('chat.placeholder_closed')
		: conn !== 'online'
			? t('chat.placeholder_offline')
			: t('chat.placeholder')

	function handleSubmit(e: Event) {
		e.preventDefault()
		const text = draft.trim()
		if (!text || !canSend) return
		sendText(text)
		setDraft('')
		const current = loadStorage(token)
		scheduleSave(token, { ...current, prefs: { ...current.prefs, draft: undefined } })
	}

	async function handleFileSelected(e: Event) {
		const input = e.currentTarget as HTMLInputElement
		const file = input.files?.[0]
		input.value = ''
		if (!file) return
		if (file.size > MAX_FILE_SIZE) {
			setFileError(t('chat.file_too_large'))
			return
		}
		setFileError(null)
		await sendFile(file)
	}

	const rootClass = `sw-panel${panelClass ? ` ${panelClass}` : ''}`

	return (
		<div
			class={rootClass}
			style={panelStyle}
			role='dialog'
			aria-label={t('chat.title')}
		>
			<div class='sw-header chat'>
				<button
					type='button'
					class='sw-icon-btn'
					aria-label={t('common.back')}
					onClick={onBack}
				>
					<BackIcon />
				</button>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div class='sw-header-title'>
						<span class='sw-header-title-text'>{headerTitle}</span>
						{conn === 'online' ? (
							<span class='sw-online-dot' aria-label={t('chat.online')} />
						) : null}
					</div>
					<div class='sw-header-sub'>{headerSub}</div>
				</div>
				<button
					type='button'
					class='sw-icon-btn'
					aria-label={t('common.close')}
					onClick={onClose}
				>
					<CloseIcon />
				</button>
			</div>

			{conn === 'reconnecting' || conn === 'offline' ? (
				<div class='sw-banner'>
					<span class='dot' />
					{t('chat.connection_lost')}
				</div>
			) : null}

			<div
				class='sw-chat-body'
				ref={bodyRef}
				role='log'
				aria-live='polite'
				aria-atomic='false'
			>
				<div class='sw-day-sep'>{t('chat.today')}</div>

				{showGreeting ? (
					<MessageBubble
						message={{
							id: 'greeting',
							direction: 'in',
							type: 'text',
							text: greeting,
							sentAt: Date.now(),
							status: 'delivered',
						}}
					/>
				) : null}

				{list.map((m, i) => {
					if (m.type === 'system') {
						return <SystemMessage key={m.id} message={m} />
					}
					const prev = list[i - 1]
					const next = list[i + 1]
					const regular = (msg: ChatMessage | undefined) =>
						msg && msg.type !== 'system' ? msg : undefined
					const prevReg = regular(prev)
					const nextReg = regular(next)
					const sameAsPrev =
						!!prevReg &&
						prevReg.direction === m.direction &&
						(prevReg.author?.id ?? prevReg.author?.name) ===
							(m.author?.id ?? m.author?.name)
					const sameAsNext =
						!!nextReg &&
						nextReg.direction === m.direction &&
						(m.author?.id ?? m.author?.name) ===
							(nextReg.author?.id ?? nextReg.author?.name)
					const sameMinute =
						!!nextReg && minuteBucket(m.sentAt) === minuteBucket(nextReg.sentAt)
					const isFirstInGroup = !sameAsPrev
					const showMeta = !sameAsNext || !sameMinute
					return (
						<MessageBubble
							key={m.id}
							message={m}
							showMeta={showMeta}
							hasTail={isFirstInGroup}
							options={m.id === answerableId ? m.options : undefined}
							optionsDisabled={optionsDisabled}
							onOptionClick={handleOptionClick}
						/>
					)
				})}

				{closed ? (
					<div class='sw-cta-row'>
						<button
							type='button'
							class='sw-cta'
							onClick={() => startNewSession()}
						>
							{t('chat.new_dialog')}
						</button>
					</div>
				) : null}

				{typing ? (
					<div class='sw-msg-row in'>
						<div class='sw-msg-group'>
							<div class='sw-typing' aria-label={t('chat.typing')}>
								<span />
								<span />
								<span />
							</div>
						</div>
					</div>
				) : null}
			</div>

			{unreadCount > 0 ? (
				<button
					type='button'
					class='sw-unread-pill'
					onClick={() => {
						bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' })
						setUnreadCount(0)
					}}
				>
					↓ {t('chat.new_messages_pill')} · {unreadCount}
				</button>
			) : null}

			{fileError ? (
				<div class='sw-file-error' role='alert'>
					<AlertCircleIcon />
					{fileError}
				</div>
			) : null}

			<form class='sw-input-bar' onSubmit={handleSubmit}>
				<input
					ref={fileInputRef}
					type='file'
					accept={ACCEPT_MIME}
					onChange={handleFileSelected}
					style={{ display: 'none' }}
				/>
				<button
					type='button'
					class='sw-attach'
					aria-label={t('chat.attach')}
					onClick={() => fileInputRef.current?.click()}
					disabled={inputDisabled}
				>
					<PaperclipIcon />
				</button>
				<textarea
					ref={textareaRef}
					class='sw-textarea'
					placeholder={placeholder}
					value={draft}
					rows={1}
					disabled={inputDisabled}
					onInput={e => {
						const text = (e.currentTarget as HTMLTextAreaElement).value
						setDraft(text)
						const current = loadStorage(token)
						scheduleSave(token, {
							...current,
							prefs: {
								...current.prefs,
								draft: text ? { text, updatedAt: Date.now() } : undefined,
							},
						})
					}}
					onKeyDown={e => {
						// На touch-устройствах Enter — это перенос строки (как обычный
						// textarea). На десктопе Enter отправляет, Shift+Enter — newline.
						if (isTouchOnly) return
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault()
							handleSubmit(e as unknown as Event)
						}
					}}
				/>
				<button
					type='submit'
					class='sw-send'
					aria-label={t('chat.send')}
					disabled={!canSend}
				>
					<SendIcon />
				</button>
			</form>
		</div>
	)
}

function MessageBubble({
	message,
	showMeta = true,
	hasTail = true,
	options,
	optionsDisabled = false,
	onOptionClick,
}: {
	message: ChatMessage
	showMeta?: boolean
	hasTail?: boolean
	options?: string[]
	optionsDisabled?: boolean
	onOptionClick?: (option: string) => void
}) {
	const side = message.direction
	return (
		<div class={`sw-msg-row ${side}`}>
			<div class={`sw-msg-group ${side}`}>
				<MessageBody message={message} hasTail={hasTail} />
				{showMeta ? <MessageMeta message={message} /> : null}
				{options?.length ? (
					<div class='sw-options'>
						{options.map((option) => (
							<button
								key={option}
								type='button'
								class='sw-option'
								disabled={optionsDisabled}
								onClick={() => onOptionClick?.(option)}
							>
								{option}
							</button>
						))}
					</div>
				) : null}
			</div>
		</div>
	)
}

function minuteBucket(ts: number): number {
	return Math.floor(ts / 60_000)
}

function MessageBody({
	message,
	hasTail,
}: {
	message: ChatMessage
	hasTail: boolean
}) {
	const side = message.direction
	const tailCls = hasTail ? ' tail' : ''
	const [imgFailed, setImgFailed] = useState(false)
	const isFileType =
		message.type === 'photo' ||
		message.type === 'document' ||
		message.type === 'audio' ||
		message.type === 'video'
	const caption = isFileType && message.text ? message.text.trim() : ''
	if (
		message.type === 'photo' &&
		message.fileUrl &&
		looksLikeImage(message) &&
		!imgFailed
	) {
		const fileUrl = message.fileUrl
		const alt = message.fileName ?? ''
		// Photo stage: object-fit:contain поверх blurred-cover-копии того же src.
		// Открытие — внутренний Lightbox (lightboxUrl signal в state/widget),
		// чтобы не светить /api/widget/file/:id?token=... в URL bar партнёра.
		const photoStage = (
			<button
				type='button'
				class='sw-media-photo'
				aria-label={alt || 'Open photo'}
				onClick={() => {
					lightboxUrl.value = fileUrl
					lightboxAlt.value = alt
				}}
			>
				{/* Blur-cover как <img>, а не CSS background-image: тот же src
				    переиспользует уже декодированный bitmap (одна сетевая
				    загрузка, один decode). loading=lazy откладывает оффскрин-
				    фотки в длинной истории — без этого credentials-set с
				    десятком фото блокирует main thread на decode'ах. */}
				<img
					class='sw-media-photo__blur'
					src={fileUrl}
					alt=''
					aria-hidden='true'
					loading='lazy'
					decoding='async'
					draggable={false}
				/>
				<img
					class='sw-media-photo__img'
					src={fileUrl}
					alt={alt}
					loading='lazy'
					decoding='async'
					onError={() => setImgFailed(true)}
				/>
			</button>
		)
		// Photo + caption живут в одном media-bubble (общий background, без gap)
		// — паттерн Telegram. Хвостик — на самом контейнере.
		return (
			<div class={`sw-media-bubble ${side}${tailCls}`}>
				{photoStage}
				{caption ? (
					<div class='sw-media-bubble__caption'>{caption}</div>
				) : null}
			</div>
		)
	}
	if (isFileType) {
		const label =
			(message.fileName && message.fileName.trim()) ||
			t(`chat.file_type.${message.type}`)
		const size = message.fileSize ? (
			<div class='sw-bubble-file__size'>{formatSize(message.fileSize)}</div>
		) : null
		const body = (
			<>
				<div class='sw-bubble-file__icon'>
					<PaperclipIcon />
				</div>
				<div class='sw-bubble-file__info'>
					<div class='sw-bubble-file__name'>{label}</div>
					{size}
				</div>
			</>
		)
		// File-bubble не full-width (icon+name), объединять с caption в общий
		// контейнер визуально нет смысла — оставляем два sibling'а в group.
		const mediaTailCls = caption ? '' : tailCls
		const fileBubble = message.fileUrl ? (
			<a
				class={`sw-bubble-file ${side}${mediaTailCls}`}
				href={message.fileUrl}
				target='_blank'
				rel='noopener noreferrer'
			>
				{body}
			</a>
		) : (
			<div class={`sw-bubble-file ${side}${mediaTailCls}`}>{body}</div>
		)
		if (!caption) return fileBubble
		return (
			<>
				{fileBubble}
				<div class={`sw-bubble ${side}${tailCls}`}>{caption}</div>
			</>
		)
	}
	return <div class={`sw-bubble ${side}${tailCls}`}>{message.text}</div>
}

function MessageMeta({ message }: { message: ChatMessage }) {
	if (message.id === 'greeting') return null
	const time = formatTime(message.sentAt)

	if (message.status === 'failed') {
		return (
			<div class='sw-msg-failed'>
				<AlertCircleIcon />
				<span>{t('chat.send_failed')}</span>
				<button
					type='button'
					class='sw-retry-btn'
					onClick={() => retryMessage(message.clientExternalId ?? message.id)}
					aria-label={t('chat.retry')}
				>
					{t('chat.retry')}
				</button>
			</div>
		)
	}

	return (
		<div class='sw-msg-meta'>
			<span>{time}</span>
			{message.direction === 'out' ? <Tick status={message.status} /> : null}
		</div>
	)
}

function Tick({ status }: { status: ChatMessage['status'] }) {
	if (status === 'sending') return <CheckSingleIcon />
	if (status === 'sent' || status === 'delivered') return <CheckDoubleIcon />
	if (status === 'read') return <CheckDoubleIcon class='sw-tick-read' />
	return null
}

function formatTime(ts: number): string {
	const d = new Date(ts)
	const hh = String(d.getHours()).padStart(2, '0')
	const mm = String(d.getMinutes()).padStart(2, '0')
	return `${hh}:${mm}`
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} Б`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
	return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

function SystemMessage({ message }: { message: ChatMessage }) {
	const labelKey =
		message.systemKind === 'session_closed'
			? 'chat.system.session_closed'
			: 'chat.system.session_started'
	return (
		<div class='sw-system'>
			<span>
				{t(labelKey)} · {formatTime(message.sentAt)}
			</span>
		</div>
	)
}
